import * as z from 'zod';

export const GetSubscriptionContractRequest = z.object({
  subscriptionContractId: z
    .string()
    .describe('ID of the SubscriptionContract to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetSubscriptionContractResponse = z.object({
  _id: z
    .string()
    .describe('SubscriptionContract ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the SubscriptionContract is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the SubscriptionContract.\n\nIgnored when creating a SubscriptionOrderContract.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the subscription contract was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the subscription contract was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
    )
    .max(500)
    .optional()
    .nullable(),
  subscriptionSettings: z
    .object({
      frequency: z
        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
        .describe('Frequency of recurring payment.')
        .optional(),
      interval: z
        .number()
        .int()
        .describe(
          'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
        )
        .min(1)
        .max(3650)
        .optional()
        .nullable(),
      autoRenewal: z
        .boolean()
        .describe(
          'Whether subscription is renewed automatically at the end of each period.'
        )
        .optional(),
      billingCycles: z
        .number()
        .int()
        .describe(
          'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
        )
        .min(1)
        .optional()
        .nullable(),
      enableCustomerCancellation: z
        .boolean()
        .describe('Whether to allow the customer to cancel the subscription.')
        .optional(),
      freeTrialPeriod: z
        .object({
          frequency: z
            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
            .describe('Frequency of period. Values: DAY, WEEK, MONTH, YEAR')
            .optional(),
          interval: z
            .number()
            .int()
            .describe(
              'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
            )
            .min(1)
            .max(3650)
            .optional(),
        })
        .describe('Period until first cycle starts. If None => no free trial')
        .optional(),
    })
    .describe('Subscription detailed information.')
    .optional(),
  lineItems: z
    .array(
      z.object({
        _id: z.string().describe('Line item ID.').optional(),
        productName: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
              )
              .min(1)
              .max(200)
              .optional(),
            translated: z
              .string()
              .describe(
                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
              )
              .min(1)
              .max(400)
              .optional()
              .nullable(),
          })
          .describe(
            'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
          )
          .optional(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "References to the line item's origin catalog. Empty for custom line items."
          )
          .optional(),
        quantity: z
          .number()
          .int()
          .describe('Line item quantity.')
          .min(1)
          .max(100000)
          .optional(),
        totalDiscount: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Total discount for this line item's entire quantity.")
          .optional(),
        descriptionLines: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                      )
                      .max(100)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe('Description line name.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z.never().optional(),
                }),
                z.object({
                  colorInfo: z.never().optional(),
                  plainText: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(600)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(600)
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line plain text value.'),
                }),
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(500)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      code: z
                        .string()
                        .describe('HEX or RGB color code for display.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line color value.'),
                }),
              ])
            )
          )
          .min(0)
          .max(20)
          .optional(),
        image: z.string().describe('Line item image.').optional(),
        physicalProperties: z
          .object({
            weight: z
              .number()
              .describe(
                'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
              )
              .optional()
              .nullable(),
            sku: z
              .string()
              .describe(
                'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
              )
              .max(40)
              .optional()
              .nullable(),
            shippable: z
              .boolean()
              .describe('Whether this line item is shippable.')
              .optional(),
          })
          .describe(
            'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
          )
          .optional(),
        itemType: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                preset: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                preset: z
                  .enum([
                    'UNRECOGNISED',
                    'PHYSICAL',
                    'DIGITAL',
                    'GIFT_CARD',
                    'SERVICE',
                  ])
                  .describe('Preset item type.'),
              }),
              z.object({
                preset: z.never().optional(),
                custom: z
                  .string()
                  .describe(
                    'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                  ),
              }),
            ])
          )
          .describe('Item type. Either a preset type or custom.')
          .optional(),
        fulfillerId: z
          .string()
          .describe(
            'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        refundQuantity: z
          .number()
          .int()
          .describe('Number of items that were refunded.')
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        restockQuantity: z
          .number()
          .int()
          .describe(
            'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        price: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Line item price after line item discounts for display purposes.'
          )
          .optional(),
        priceBeforeDiscounts: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
          )
          .optional(),
        totalPriceBeforeTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe('Total price after discounts, and before tax.')
          .optional(),
        totalPriceAfterTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe('Total price after all discounts and tax.')
          .optional(),
        paymentOption: z
          .enum([
            'FULL_PAYMENT_ONLINE',
            'FULL_PAYMENT_OFFLINE',
            'MEMBERSHIP',
            'DEPOSIT_ONLINE',
            'MEMBERSHIP_OFFLINE',
            'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
          ])
          .describe(
            'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
          )
          .optional(),
        taxDetails: z
          .object({
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Taxable amount of this line item.')
              .optional(),
            taxRate: z
              .string()
              .describe(
                'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
              )
              .optional(),
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The calculated tax, based on the `taxableAmount` and `taxRate`.'
              )
              .optional(),
          })
          .describe(
            'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
          )
          .optional(),
        taxInfo: z
          .object({
            taxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Calculated tax, based on `taxable_amount` and `tax_rate`.'
              )
              .optional(),
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Amount for which tax is calculated.')
              .optional(),
            taxRate: z
              .string()
              .describe('Tax rate %, as a decimal point.')
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxIncludedInPrice: z
              .boolean()
              .describe('Indicates whether the price already includes tax.')
              .optional(),
            taxBreakdown: z
              .array(
                z.object({
                  jurisdiction: z
                    .string()
                    .describe(
                      'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                    )
                    .optional()
                    .nullable(),
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount of tax calculated for this line item.')
                    .optional(),
                  taxType: z
                    .string()
                    .describe(
                      'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  taxName: z
                    .string()
                    .describe(
                      'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  jurisdictionType: z
                    .enum([
                      'UNDEFINED',
                      'COUNTRY',
                      'STATE',
                      'COUNTY',
                      'CITY',
                      'SPECIAL',
                    ])
                    .describe(
                      'Type of jurisdiction that taxes were calculated for.'
                    )
                    .optional(),
                  nonTaxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Non-taxable amount of the line item price.')
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of the line item price.')
                    .optional(),
                })
              )
              .max(7)
              .optional(),
          })
          .describe(
            'Represents all the relevant tax details for a specific line item.'
          )
          .optional(),
        digitalFile: z
          .object({
            fileId: z
              .string()
              .describe('ID of the secure file in media.')
              .min(1)
              .max(100)
              .optional(),
            link: z
              .string()
              .describe(
                'Link will exist after the digital links have been generated on the order.'
              )
              .url()
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            expirationDate: z
              .date()
              .describe('Link expiration time and date.')
              .optional()
              .nullable(),
          })
          .describe(
            'Digital file identifier, relevant only for items with type DIGITAL.'
          )
          .optional(),
        subscriptionInfo: z
          .object({
            _id: z
              .string()
              .describe('Subscription ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            cycleNumber: z
              .number()
              .int()
              .describe(
                'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
              )
              .min(0)
              .optional(),
            subscriptionOptionTitle: z
              .string()
              .describe(
                'Subscription option title. For example, `"Monthly coffee Subscription"`.'
              )
              .min(0)
              .max(150)
              .optional(),
            subscriptionOptionDescription: z
              .string()
              .describe(
                'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
              )
              .max(500)
              .optional()
              .nullable(),
            subscriptionSettings: z
              .object({
                frequency: z
                  .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                  .describe('Frequency of recurring payment.')
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                  )
                  .min(1)
                  .max(3650)
                  .optional()
                  .nullable(),
                autoRenewal: z
                  .boolean()
                  .describe(
                    'Whether subscription is renewed automatically at the end of each period.'
                  )
                  .optional(),
                billingCycles: z
                  .number()
                  .int()
                  .describe(
                    'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                  )
                  .min(1)
                  .optional()
                  .nullable(),
                enableCustomerCancellation: z
                  .boolean()
                  .describe(
                    'Whether to allow the customer to cancel the subscription.'
                  )
                  .optional(),
                freeTrialPeriod: z
                  .object({
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe(
                        'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                      )
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                      )
                      .min(1)
                      .max(3650)
                      .optional(),
                  })
                  .describe(
                    'Period until first cycle starts. If None => no free trial'
                  )
                  .optional(),
              })
              .describe('Subscription detailed information.')
              .optional(),
            chargesDescription: z
              .string()
              .describe(
                'Description of the charges that will be applied for subscription.'
              )
              .max(1000)
              .optional()
              .nullable(),
          })
          .describe('Subscription info.')
          .optional(),
        priceDescription: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
              )
              .min(1)
              .max(100)
              .optional(),
            translated: z
              .string()
              .describe(
                "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
          )
          .optional(),
        depositAmount: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
          )
          .optional(),
        locations: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Location id in the associated owner app.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              appId: z
                .string()
                .describe(
                  'Location owner app, if not provided then the site business info locations will be used.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe('Quantity for specific location.')
                .min(1)
                .max(100000)
                .optional(),
              name: z
                .string()
                .describe('Location name.')
                .max(500)
                .optional()
                .nullable(),
            })
          )
          .min(0)
          .max(5)
          .optional(),
        lineItemPrice: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Total price **after** catalog discounts and line item discounts.'
          )
          .optional(),
        taxableAddress: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ addressType: z.never().optional() }),
              z.object({
                addressType: z
                  .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                  .describe(
                    'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                  ),
              }),
            ])
          )
          .describe('Address used for tax calculation.')
          .optional(),
        priceUndetermined: z
          .boolean()
          .describe(
            'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
          )
          .optional(),
        fixedQuantity: z
          .boolean()
          .describe(
            'Whether the line item quantity is fixed and cannot be changed.\n\nDefault: `false`'
          )
          .optional(),
        decimalQuantity: z
          .intersection(
            z.object({
              value: z
                .string()
                .describe(
                  'The decimal quantity the buyer / merchant entered.\n\nString-encoded decimal. The exact value is preserved — no\nrounding, no conversion to an integer.\n\nExamples: "2.75", "1.5", "0.75".'
                )
                .optional(),
              description: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Human-readable, pre-formatted display string for the quantity. For example, "2.75 square meters of fake grass".\nAlways set alongside `value`; use this directly for display rather than composing your own string from `value` and `unit`.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                measurementUnit: z.never().optional(),
                otherUnit: z.never().optional(),
              }),
              z.object({
                otherUnit: z.never().optional(),
                measurementUnit: z
                  .enum([
                    'UNSPECIFIED',
                    'ML',
                    'CL',
                    'L',
                    'CBM',
                    'MG',
                    'G',
                    'KG',
                    'MM',
                    'CM',
                    'M',
                    'SQM',
                    'OZ',
                    'LB',
                    'FLOZ',
                    'PT',
                    'QT',
                    'GAL',
                    'IN',
                    'FT',
                    'YD',
                    'SQFT',
                  ])
                  .describe(
                    'Unit the value is expressed in, selected from the standard `measurementUnit` enum.'
                  ),
              }),
              z.object({
                measurementUnit: z.never().optional(),
                otherUnit: z
                  .string()
                  .describe(
                    'Free-form unit name for standard units not in the\n`measurementUnit` enum (e.g. "hour", "minute", "session").'
                  )
                  .min(1)
                  .max(50),
              }),
            ])
          )
          .describe(
            'Decimal-quantity metadata. Set when the line represents a\nnon-integer purchase (e.g. 2.75 m² of fake grass).\n\nWhen set, the line item must have `fixedQuantity` = true and be set with `quantity` = 1.\nThe decimal value lives in `decimalQuantity.value`.'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        modifierGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Modifier group ID.')
                .min(1)
                .max(36)
                .optional(),
              name: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe('Modifier group name.')
                .optional(),
              modifiers: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe('The quantity of this modifier.')
                      .min(1)
                      .max(100000)
                      .optional()
                      .nullable(),
                    label: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Primary display label for the modifier.')
                      .optional(),
                    details: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Additional details.')
                      .optional(),
                    price: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('The price of the modifier.')
                      .optional(),
                  })
                )
                .min(1)
                .max(10)
                .optional(),
            })
          )
          .max(10)
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  buyerInfo: z
    .intersection(
      z.object({
        contactId: z
          .string()
          .describe(
            "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        email: z
          .string()
          .describe("Buyer's email address.")
          .email()
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          visitorId: z.never().optional(),
          memberId: z.never().optional(),
        }),
        z.object({
          memberId: z.never().optional(),
          visitorId: z
            .string()
            .describe(
              "Visitor ID. Returned when the buyer isn't a logged-in site member."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          visitorId: z.never().optional(),
          memberId: z
            .string()
            .describe(
              'Member ID. Returned when the buyer is a logged-in site member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe('Buyer information.')
    .optional(),
  buyerLanguage: z
    .string()
    .describe(
      'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
    )
    .max(10)
    .optional()
    .nullable(),
  weightUnit: z
    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
    .describe("Weight measurement unit - defaults to site's weight unit.")
    .optional(),
  currency: z
    .string()
    .describe(
      'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
    )
    .optional()
    .nullable(),
  currencyConversionDetails: z
    .object({
      originalCurrency: z
        .string()
        .describe(
          "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
        )
        .optional()
        .nullable(),
      conversionRate: z
        .string()
        .describe(
          'The rate used for converting the original currency to the currency used for payment.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Multi-currency information of the subscription. conversionRate may reflect updated exchange rate on created order per recurring cycle.'
    )
    .optional(),
  taxIncludedInPrices: z
    .boolean()
    .describe(
      'Whether tax is included in line item prices.\n\nDefault: `false`'
    )
    .optional(),
  priceSummary: z
    .object({
      subtotal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Subtotal of all line items, before discounts and before tax.'
        )
        .optional(),
      shipping: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total shipping price, before discounts and before tax.')
        .optional(),
      tax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax applied to the order.')
        .optional(),
      discount: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total discount amount applied to the order.')
        .optional(),
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe("Order's total price after discounts and tax.")
        .optional(),
      totalAdditionalFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total additional fees before tax.')
        .optional(),
    })
    .describe('Order price summary.')
    .optional(),
  platformFeeSummary: z
    .object({
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total sum of all platform fees.')
        .optional(),
      totalPassOn: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total amount of platform fees with `PASS_ON` charge type.')
        .optional(),
      totalAbsorbed: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total amount of platform fees with `ABSORBED` charge type.')
        .optional(),
      fees: z
        .array(
          z.object({
            name: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe('Platform fee name.')
              .optional(),
            amount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Platform fee amount.')
              .optional(),
            lineItemId: z
              .string()
              .describe('ID of the line item the platform fee applies to.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            chargeType: z
              .enum(['PASS_ON', 'ABSORBED'])
              .describe('Platform fee charge type.')
              .optional(),
            percentageRate: z
              .string()
              .describe(
                'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
              )
              .optional(),
          })
        )
        .max(300)
        .optional(),
    })
    .describe(
      'Summary of platform fees for this subscription contract, including totals by charge type and a breakdown of individual fees.'
    )
    .optional(),
  billingInfo: z
    .object({
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      contactDetails: z
        .object({
          firstName: z
            .string()
            .describe('First name.')
            .max(100)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe('Last name.')
            .max(100)
            .optional()
            .nullable(),
          phone: z.string().describe('Phone number.').optional().nullable(),
          company: z
            .string()
            .describe('Company name.')
            .max(1000)
            .optional()
            .nullable(),
          vatId: z
            .object({
              _id: z.string().describe("Customer's tax ID.").optional(),
              type: z
                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                .describe(
                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                )
                .optional(),
            })
            .describe(
              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
            )
            .optional(),
        })
        .describe('Contact details.')
        .optional(),
    })
    .describe('Billing address and contact details.')
    .optional(),
  shippingInfo: z
    .object({
      carrierId: z
        .string()
        .describe(
          'App Def Id of external provider which was a source of shipping info'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      code: z
        .string()
        .describe(
          'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      title: z
        .string()
        .describe(
          'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
        )
        .max(250)
        .optional(),
      logistics: z
        .intersection(
          z.object({
            deliveryTime: z
              .string()
              .describe(
                'Expected delivery time in free text. For example, `"3-5 business days"`.'
              )
              .max(500)
              .optional()
              .nullable(),
            instructions: z
              .string()
              .describe(
                'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
              )
              .max(1000)
              .optional()
              .nullable(),
            deliverByDate: z
              .date()
              .describe(
                'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            deliveryTimeSlot: z
              .object({
                from: z
                  .date()
                  .describe('Delivery slot starting time.')
                  .optional()
                  .nullable(),
                to: z
                  .date()
                  .describe('Delivery slot ending time.')
                  .optional()
                  .nullable(),
              })
              .describe('Expected delivery time slot with start and end times.')
              .optional(),
          }),
          z.xor([
            z.object({
              shippingDestination: z.never().optional(),
              pickupDetails: z.never().optional(),
            }),
            z.object({
              pickupDetails: z.never().optional(),
              shippingDestination: z
                .object({
                  address: z
                    .object({
                      streetAddress: z
                        .object({
                          name: z.string().optional(),
                          number: z.string().optional(),
                        })
                        .optional(),
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe('Address.')
                    .optional(),
                  contactDetails: z
                    .object({
                      firstName: z
                        .string()
                        .describe('First name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe('Last name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Phone number.')
                        .optional()
                        .nullable(),
                      company: z
                        .string()
                        .describe('Company name.')
                        .max(1000)
                        .optional()
                        .nullable(),
                      vatId: z
                        .object({
                          _id: z
                            .string()
                            .describe("Customer's tax ID.")
                            .optional(),
                          type: z
                            .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                            .describe(
                              'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                            )
                            .optional(),
                        })
                        .describe(
                          'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                        )
                        .optional(),
                    })
                    .describe('Contact details.')
                    .optional(),
                })
                .describe(
                  "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                ),
            }),
            z.object({
              shippingDestination: z.never().optional(),
              pickupDetails: z
                .object({
                  address: z
                    .object({
                      streetAddress: z
                        .object({
                          name: z.string().optional(),
                          number: z.string().optional(),
                        })
                        .optional(),
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe('Pickup address.')
                    .optional(),
                  pickupMethod: z
                    .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                    .describe('Pickup method')
                    .optional(),
                })
                .describe(
                  'Pickup details for store pickup or pickup point orders.'
                ),
            }),
          ])
        )
        .describe('Shipping logistics.')
        .optional(),
      cost: z
        .object({
          price: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Shipping price for display purposes.')
            .optional(),
          totalPriceBeforeTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total price of shipping after discounts (when relevant), and before tax.'
            )
            .optional(),
          totalPriceAfterTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Shipping price after all discounts (if any exist), and after tax.'
            )
            .optional(),
          taxDetails: z
            .object({
              taxableAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Taxable amount of this line item.')
                .optional(),
              taxRate: z
                .string()
                .describe(
                  'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                )
                .optional(),
              totalTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                )
                .optional(),
            })
            .describe('Tax details.')
            .optional(),
          taxInfo: z
            .object({
              taxAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                )
                .optional(),
              taxableAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Amount for which tax is calculated.')
                .optional(),
              taxRate: z
                .string()
                .describe('Tax rate %, as a decimal point.')
                .optional()
                .nullable(),
              taxGroupId: z
                .string()
                .describe('Tax group ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              taxIncludedInPrice: z
                .boolean()
                .describe('Indicates whether the price already includes tax.')
                .optional(),
              taxBreakdown: z
                .array(
                  z.object({
                    jurisdiction: z
                      .string()
                      .describe(
                        'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    rate: z
                      .string()
                      .describe(
                        'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                      )
                      .optional()
                      .nullable(),
                    taxAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Amount of tax calculated for this line item.')
                      .optional(),
                    taxType: z
                      .string()
                      .describe(
                        'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    taxName: z
                      .string()
                      .describe(
                        'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    jurisdictionType: z
                      .enum([
                        'UNDEFINED',
                        'COUNTRY',
                        'STATE',
                        'COUNTY',
                        'CITY',
                        'SPECIAL',
                      ])
                      .describe(
                        'Type of jurisdiction that taxes were calculated for.'
                      )
                      .optional(),
                    nonTaxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Non-taxable amount of the line item price.')
                      .optional(),
                    taxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Taxable amount of the line item price.')
                      .optional(),
                  })
                )
                .max(7)
                .optional(),
            })
            .describe('Represents all the relevant tax details for a shipping.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Shipping discount before tax.')
            .optional(),
        })
        .describe('Shipping costs.')
        .optional(),
      region: z
        .object({
          name: z
            .string()
            .describe(
              'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
            )
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Shipping region.')
        .optional(),
    })
    .describe('Shipping info and selected shipping option details.')
    .optional(),
  appliedDiscounts: z
    .array(
      z.intersection(
        z.object({
          discountType: z
            .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
            .optional(),
          lineItemIds: z.array(z.string()).min(0).max(300).optional(),
          _id: z
            .string()
            .describe('Discount ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          lineItemDiscounts: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Line item ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                totalDiscount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total discount amount for this line item.')
                  .optional(),
              })
            )
            .max(300)
            .optional(),
        }),
        z.xor([
          z.object({
            coupon: z.never().optional(),
            merchantDiscount: z.never().optional(),
            discountRule: z.never().optional(),
          }),
          z.object({
            merchantDiscount: z.never().optional(),
            discountRule: z.never().optional(),
            coupon: z
              .object({
                _id: z.string().describe('Coupon ID.').optional(),
                code: z.string().describe('Coupon code.').optional(),
                name: z.string().describe('Coupon name.').optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Coupon value.')
                  .optional(),
              })
              .describe('Coupon applied by the customer.'),
          }),
          z.object({
            coupon: z.never().optional(),
            discountRule: z.never().optional(),
            merchantDiscount: z
              .intersection(
                z.object({
                  amount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Discount amount.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    discountReason: z.never().optional(),
                    description: z.never().optional(),
                  }),
                  z.object({
                    description: z.never().optional(),
                    discountReason: z
                      .enum([
                        'UNSPECIFIED',
                        'EXCHANGED_ITEMS',
                        'BILLING_ADJUSTMENT',
                      ])
                      .describe('Predefined discount reason.'),
                  }),
                  z.object({
                    discountReason: z.never().optional(),
                    description: z
                      .string()
                      .describe('Custom discount description as free text.')
                      .max(200),
                  }),
                ])
              )
              .describe('Discount applied manually by the merchant.'),
          }),
          z.object({
            coupon: z.never().optional(),
            merchantDiscount: z.never().optional(),
            discountRule: z
              .object({
                _id: z
                  .string()
                  .describe('Discount rule ID')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Original discount rule name (in site's default language)."
                      )
                      .min(1)
                      .max(256)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                      )
                      .min(1)
                      .max(500)
                      .optional()
                      .nullable(),
                  })
                  .describe('Discount rule name')
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Discount value.')
                  .optional(),
              })
              .describe(
                'Automatic discount applied by the system based on configured discount rules.'
              ),
          }),
        ])
      )
    )
    .max(100)
    .optional(),
  channelInfo: z
    .object({
      type: z
        .enum([
          'UNSPECIFIED',
          'WEB',
          'POS',
          'EBAY',
          'AMAZON',
          'OTHER_PLATFORM',
          'WIX_APP_STORE',
          'WIX_INVOICES',
          'BACKOFFICE_MERCHANT',
          'WISH',
          'CLASS_PASS',
          'GLOBAL_E',
          'FACEBOOK',
          'ETSY',
          'TIKTOK',
          'FAIRE_COM',
          'PAYPAL_AGENTIC_CHECKOUT',
          'STRIPE_AGENTIC_CHECKOUT',
        ])
        .describe(
          'Sales channel that submitted the order.\n\nWhen creating an order via the API to record an external order, use the channel type that matches the original source. If no matching type exists, use `OTHER_PLATFORM`.'
        )
        .optional(),
      externalOrderId: z
        .string()
        .describe(
          'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
        )
        .max(100)
        .optional()
        .nullable(),
      externalOrderUrl: z
        .string()
        .describe(
          'URL to the order in the external system. Relevant when recording orders from external platforms.'
        )
        .max(300)
        .optional()
        .nullable(),
    })
    .describe('Information about the sales channel that submitted this order.')
    .optional(),
  customFields: z
    .array(
      z.object({
        value: z.any().describe('Custom field value.').optional().nullable(),
        title: z
          .string()
          .describe('Custom field title.')
          .min(1)
          .max(500)
          .optional(),
        translatedTitle: z
          .string()
          .describe('Translated custom field title.')
          .min(1)
          .max(500)
          .optional()
          .nullable(),
      })
    )
    .max(100)
    .optional(),
  recipientInfo: z
    .object({
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      contactDetails: z
        .object({
          firstName: z
            .string()
            .describe('First name.')
            .max(100)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe('Last name.')
            .max(100)
            .optional()
            .nullable(),
          phone: z.string().describe('Phone number.').optional().nullable(),
          company: z
            .string()
            .describe('Company name.')
            .max(1000)
            .optional()
            .nullable(),
          vatId: z
            .object({
              _id: z.string().describe("Customer's tax ID.").optional(),
              type: z
                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                .describe(
                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                )
                .optional(),
            })
            .describe(
              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
            )
            .optional(),
        })
        .describe('Contact details.')
        .optional(),
    })
    .describe(
      'Order recipient address and contact details.\n\nThis field may differ from the address in `shippingInfo.logistics` when:\n+ The chosen shipping option is pickup point or store pickup.\n+ No shipping option is selected.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the subscription contract object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the [app dashboard](https://dev.wix.com/dc3/my-apps/) before they can be accessed with API calls.'
    )
    .optional(),
  title: z
    .string()
    .describe(
      'Subscription option title. For example, `"Monthly coffee Subscription"`.'
    )
    .min(0)
    .max(150)
    .optional()
    .nullable(),
  additionalFees: z
    .array(
      z.object({
        code: z
          .string()
          .describe("Additional fee's unique code for future processing.")
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Name of additional fee.')
          .min(1)
          .max(50)
          .optional(),
        price: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price.")
          .optional(),
        taxDetails: z
          .object({
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Taxable amount of this line item.')
              .optional(),
            taxRate: z
              .string()
              .describe(
                'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
              )
              .optional(),
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The calculated tax, based on the `taxableAmount` and `taxRate`.'
              )
              .optional(),
          })
          .describe('Tax details.')
          .optional(),
        taxInfo: z
          .object({
            taxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Calculated tax, based on `taxable_amount` and `tax_rate`.'
              )
              .optional(),
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Amount for which tax is calculated.')
              .optional(),
            taxRate: z
              .string()
              .describe('Tax rate %, as a decimal point.')
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxIncludedInPrice: z
              .boolean()
              .describe('Indicates whether the price already includes tax.')
              .optional(),
            taxBreakdown: z
              .array(
                z.object({
                  jurisdiction: z
                    .string()
                    .describe(
                      'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                    )
                    .optional()
                    .nullable(),
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount of tax calculated for this line item.')
                    .optional(),
                  taxType: z
                    .string()
                    .describe(
                      'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  taxName: z
                    .string()
                    .describe(
                      'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  jurisdictionType: z
                    .enum([
                      'UNDEFINED',
                      'COUNTRY',
                      'STATE',
                      'COUNTY',
                      'CITY',
                      'SPECIAL',
                    ])
                    .describe(
                      'Type of jurisdiction that taxes were calculated for.'
                    )
                    .optional(),
                  nonTaxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Non-taxable amount of the line item price.')
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of the line item price.')
                    .optional(),
                })
              )
              .max(7)
              .optional(),
          })
          .describe(
            'Represents all the relevant tax details for additional fee.'
          )
          .optional(),
        providerAppId: z
          .string()
          .describe("SPI implementer's `appId`.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        priceBeforeTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price before tax.")
          .optional(),
        priceAfterTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price after tax.")
          .optional(),
        _id: z
          .string()
          .describe("Additional fee's id.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        lineItemIds: z.array(z.string()).optional(),
        source: z
          .enum(['SERVICE_PLUGIN', 'ITEM', 'MANUAL', 'SHIPPING', 'PLATFORM'])
          .describe('Specifies the entity that added the additional fee.')
          .optional(),
        taxableAddress: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ addressType: z.never().optional() }),
              z.object({
                addressType: z
                  .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                  .describe(
                    'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                  ),
              }),
            ])
          )
          .describe('Address type tax was calculated against for this fee.')
          .optional(),
      })
    )
    .max(100)
    .optional(),
  activities: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Activity ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('When the activity occurred.')
            .optional()
            .nullable(),
          author: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({
                  userId: z.never().optional(),
                  appId: z.never().optional(),
                }),
                z.object({
                  appId: z.never().optional(),
                  userId: z
                    .string()
                    .describe(
                      'User ID. When the draft order was created by a Wix user.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  userId: z.never().optional(),
                  appId: z
                    .string()
                    .describe(
                      'App ID. When the draft order was created by an app.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe("Identity of the draft order's initiator.")
            .optional(),
          activityType: z
            .enum(['UNKNOWN_ACTIVITY_TYPE', 'CHANGES_APPLIED'])
            .describe('Activity type.')
            .optional(),
        }),
        z.intersection(
          z.xor([
            z.object({ contractTarget: z.never().optional() }),
            z.object({
              contractTarget: z
                .object({
                  nextCycleNumber: z
                    .number()
                    .int()
                    .describe(
                      'Cycle number of the next billing cycle (1-based). Set by the caller; returned by the server on read.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Scopes the draft to the contract level, applying to all future cycles.'
                ),
            }),
          ]),
          z.xor([
            z.object({ changesApplied: z.never().optional() }),
            z.object({
              changesApplied: z
                .intersection(
                  z.object({
                    draftSubscriptionContractId: z
                      .string()
                      .describe(
                        'ID of the DraftSubscriptionContract that was committed.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    changes: z
                      .array(
                        z.intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                            }),
                            z.object({
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              lineItemChanged: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe('Line item ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name after change.')
                                    .optional(),
                                  quantity: z
                                    .object({
                                      originalQuantity: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Original item quantity before the change.'
                                        )
                                        .min(0)
                                        .max(1000000)
                                        .optional(),
                                      newQuantity: z
                                        .number()
                                        .int()
                                        .describe(
                                          'New item quantity after the change.'
                                        )
                                        .min(0)
                                        .max(1000000)
                                        .optional(),
                                      diff: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Difference between new and original quantities.'
                                        )
                                        .min(1)
                                        .max(1000000)
                                        .optional(),
                                      deltaType: z
                                        .enum([
                                          'QUANTITY_INCREASED',
                                          'QUANTITY_DECREASED',
                                        ])
                                        .describe(
                                          'Direction of the quantity change.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Item quantity change.')
                                    .optional(),
                                })
                                .describe('Line item was modified.'),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              lineItemAdded: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe('Line item ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  quantity: z
                                    .number()
                                    .int()
                                    .describe('Added or removed item quantity.')
                                    .min(1)
                                    .max(1000000)
                                    .optional(),
                                })
                                .describe(
                                  'Line item was added to the contract.'
                                ),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              lineItemRemoved: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe('Line item ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  quantity: z
                                    .number()
                                    .int()
                                    .describe('Added or removed item quantity.')
                                    .min(1)
                                    .max(1000000)
                                    .optional(),
                                })
                                .describe(
                                  'Line item was removed from the contract.'
                                ),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              discountAdded: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe('Discount ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          'Value in site default language.'
                                        )
                                        .min(1)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe('Translated value.')
                                        .min(1)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Discount name.')
                                    .optional(),
                                  totalAmount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Discount amount.')
                                    .optional(),
                                })
                                .describe(
                                  'Discount was added to the contract.'
                                ),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              discountRemoved: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe('Discount ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          'Value in site default language.'
                                        )
                                        .min(1)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe('Translated value.')
                                        .min(1)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Discount name.')
                                    .optional(),
                                  totalAmount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Discount amount.')
                                    .optional(),
                                })
                                .describe(
                                  'Discount was removed from the contract.'
                                ),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              additionalFeeAdded: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe('Additional fee ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          'Value in site default language.'
                                        )
                                        .min(1)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe('Translated value.')
                                        .min(1)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Additional fee name.')
                                    .optional(),
                                  totalAmount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Additional fee amount.')
                                    .optional(),
                                })
                                .describe(
                                  'Additional fee was added to the contract.'
                                ),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              additionalFeeRemoved: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe('Additional fee ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          'Value in site default language.'
                                        )
                                        .min(1)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe('Translated value.')
                                        .min(1)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Additional fee name.')
                                    .optional(),
                                  totalAmount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Additional fee amount.')
                                    .optional(),
                                })
                                .describe(
                                  'Additional fee was removed from the contract.'
                                ),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z
                                .object({
                                  originalTotal: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Total price before the commit.')
                                    .optional(),
                                  newTotal: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Total price after the commit.')
                                    .optional(),
                                })
                                .describe('Total price changed.'),
                            }),
                          ])
                        )
                      )
                      .max(100)
                      .optional(),
                    reason: z
                      .string()
                      .describe(
                        'Reason for the edit, provided by the merchant (optional).'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                  }),
                  z.xor([
                    z.object({ contractTarget: z.never().optional() }),
                    z.object({
                      contractTarget: z
                        .object({
                          nextCycleNumber: z
                            .number()
                            .int()
                            .describe(
                              'Cycle number of the next billing cycle (1-based). Set by the caller; returned by the server on read.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Scopes the draft to the contract level, applying to all future cycles.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'Changes committed from a DraftSubscriptionContract.'
                ),
            }),
          ])
        )
      )
    )
    .max(500)
    .optional(),
});
export const QuerySubscriptionContractsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`\nLearn more about the [filter format](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('WQL expression.'),
});
export const QuerySubscriptionContractsResponse = z.object({
  subscriptionContracts: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('SubscriptionContract ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the SubscriptionContract is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the SubscriptionContract.\n\nIgnored when creating a SubscriptionOrderContract.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the subscription contract was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the subscription contract was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
          )
          .max(500)
          .optional()
          .nullable(),
        subscriptionSettings: z
          .object({
            frequency: z
              .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
              .describe('Frequency of recurring payment.')
              .optional(),
            interval: z
              .number()
              .int()
              .describe(
                'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
              )
              .min(1)
              .max(3650)
              .optional()
              .nullable(),
            autoRenewal: z
              .boolean()
              .describe(
                'Whether subscription is renewed automatically at the end of each period.'
              )
              .optional(),
            billingCycles: z
              .number()
              .int()
              .describe(
                'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
              )
              .min(1)
              .optional()
              .nullable(),
            enableCustomerCancellation: z
              .boolean()
              .describe(
                'Whether to allow the customer to cancel the subscription.'
              )
              .optional(),
            freeTrialPeriod: z
              .object({
                frequency: z
                  .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                  .describe(
                    'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                  )
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                  )
                  .min(1)
                  .max(3650)
                  .optional(),
              })
              .describe(
                'Period until first cycle starts. If None => no free trial'
              )
              .optional(),
          })
          .describe('Subscription detailed information.')
          .optional(),
        lineItems: z
          .array(
            z.object({
              _id: z.string().describe('Line item ID.').optional(),
              productName: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
                )
                .optional(),
              catalogReference: z
                .object({
                  catalogItemId: z
                    .string()
                    .describe(
                      'ID of the item within the catalog it belongs to.'
                    )
                    .min(1)
                    .max(36)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                    )
                    .min(1)
                    .optional(),
                  options: z
                    .record(z.string(), z.any())
                    .describe(
                      'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "References to the line item's origin catalog. Empty for custom line items."
                )
                .optional(),
              quantity: z
                .number()
                .int()
                .describe('Line item quantity.')
                .min(1)
                .max(100000)
                .optional(),
              totalDiscount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  "Total discount for this line item's entire quantity."
                )
                .optional(),
              descriptionLines: z
                .array(
                  z.intersection(
                    z.object({
                      name: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(100)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line name.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        plainText: z.never().optional(),
                        colorInfo: z.never().optional(),
                      }),
                      z.object({
                        colorInfo: z.never().optional(),
                        plainText: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                              )
                              .max(600)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                              )
                              .max(600)
                              .optional()
                              .nullable(),
                          })
                          .describe('Description line plain text value.'),
                      }),
                      z.object({
                        plainText: z.never().optional(),
                        colorInfo: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                              )
                              .max(500)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                              )
                              .max(500)
                              .optional()
                              .nullable(),
                            code: z
                              .string()
                              .describe('HEX or RGB color code for display.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Description line color value.'),
                      }),
                    ])
                  )
                )
                .min(0)
                .max(20)
                .optional(),
              image: z.string().describe('Line item image.').optional(),
              physicalProperties: z
                .object({
                  weight: z
                    .number()
                    .describe(
                      'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                    )
                    .optional()
                    .nullable(),
                  sku: z
                    .string()
                    .describe(
                      'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                    )
                    .max(40)
                    .optional()
                    .nullable(),
                  shippable: z
                    .boolean()
                    .describe('Whether this line item is shippable.')
                    .optional(),
                })
                .describe(
                  'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
                )
                .optional(),
              itemType: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      preset: z.never().optional(),
                      custom: z.never().optional(),
                    }),
                    z.object({
                      custom: z.never().optional(),
                      preset: z
                        .enum([
                          'UNRECOGNISED',
                          'PHYSICAL',
                          'DIGITAL',
                          'GIFT_CARD',
                          'SERVICE',
                        ])
                        .describe('Preset item type.'),
                    }),
                    z.object({
                      preset: z.never().optional(),
                      custom: z
                        .string()
                        .describe(
                          'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                        ),
                    }),
                  ])
                )
                .describe('Item type. Either a preset type or custom.')
                .optional(),
              fulfillerId: z
                .string()
                .describe(
                  'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              refundQuantity: z
                .number()
                .int()
                .describe('Number of items that were refunded.')
                .min(0)
                .max(100000)
                .optional()
                .nullable(),
              restockQuantity: z
                .number()
                .int()
                .describe(
                  'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
                )
                .min(0)
                .max(100000)
                .optional()
                .nullable(),
              price: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Line item price after line item discounts for display purposes.'
                )
                .optional(),
              priceBeforeDiscounts: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
                )
                .optional(),
              totalPriceBeforeTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Total price after discounts, and before tax.')
                .optional(),
              totalPriceAfterTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Total price after all discounts and tax.')
                .optional(),
              paymentOption: z
                .enum([
                  'FULL_PAYMENT_ONLINE',
                  'FULL_PAYMENT_OFFLINE',
                  'MEMBERSHIP',
                  'DEPOSIT_ONLINE',
                  'MEMBERSHIP_OFFLINE',
                  'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                ])
                .describe(
                  'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
                )
                .optional(),
              taxDetails: z
                .object({
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of this line item.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                    )
                    .optional(),
                  totalTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                    )
                    .optional(),
                })
                .describe(
                  'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
                )
                .optional(),
              taxInfo: z
                .object({
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                    )
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount for which tax is calculated.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax rate %, as a decimal point.')
                    .optional()
                    .nullable(),
                  taxGroupId: z
                    .string()
                    .describe('Tax group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  taxIncludedInPrice: z
                    .boolean()
                    .describe(
                      'Indicates whether the price already includes tax.'
                    )
                    .optional(),
                  taxBreakdown: z
                    .array(
                      z.object({
                        jurisdiction: z
                          .string()
                          .describe(
                            'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        rate: z
                          .string()
                          .describe(
                            'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                          )
                          .optional()
                          .nullable(),
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Amount of tax calculated for this line item.'
                          )
                          .optional(),
                        taxType: z
                          .string()
                          .describe(
                            'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        taxName: z
                          .string()
                          .describe(
                            'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        jurisdictionType: z
                          .enum([
                            'UNDEFINED',
                            'COUNTRY',
                            'STATE',
                            'COUNTY',
                            'CITY',
                            'SPECIAL',
                          ])
                          .describe(
                            'Type of jurisdiction that taxes were calculated for.'
                          )
                          .optional(),
                        nonTaxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Non-taxable amount of the line item price.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of the line item price.')
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe(
                  'Represents all the relevant tax details for a specific line item.'
                )
                .optional(),
              digitalFile: z
                .object({
                  fileId: z
                    .string()
                    .describe('ID of the secure file in media.')
                    .min(1)
                    .max(100)
                    .optional(),
                  link: z
                    .string()
                    .describe(
                      'Link will exist after the digital links have been generated on the order.'
                    )
                    .url()
                    .min(1)
                    .max(2000)
                    .optional()
                    .nullable(),
                  expirationDate: z
                    .date()
                    .describe('Link expiration time and date.')
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Digital file identifier, relevant only for items with type DIGITAL.'
                )
                .optional(),
              subscriptionInfo: z
                .object({
                  _id: z
                    .string()
                    .describe('Subscription ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  cycleNumber: z
                    .number()
                    .int()
                    .describe(
                      'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
                    )
                    .min(0)
                    .optional(),
                  subscriptionOptionTitle: z
                    .string()
                    .describe(
                      'Subscription option title. For example, `"Monthly coffee Subscription"`.'
                    )
                    .min(0)
                    .max(150)
                    .optional(),
                  subscriptionOptionDescription: z
                    .string()
                    .describe(
                      'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  subscriptionSettings: z
                    .object({
                      frequency: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Frequency of recurring payment.')
                        .optional(),
                      interval: z
                        .number()
                        .int()
                        .describe(
                          'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                        )
                        .min(1)
                        .max(3650)
                        .optional()
                        .nullable(),
                      autoRenewal: z
                        .boolean()
                        .describe(
                          'Whether subscription is renewed automatically at the end of each period.'
                        )
                        .optional(),
                      billingCycles: z
                        .number()
                        .int()
                        .describe(
                          'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      enableCustomerCancellation: z
                        .boolean()
                        .describe(
                          'Whether to allow the customer to cancel the subscription.'
                        )
                        .optional(),
                      freeTrialPeriod: z
                        .object({
                          frequency: z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .describe(
                              'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                            )
                            .optional(),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                            )
                            .min(1)
                            .max(3650)
                            .optional(),
                        })
                        .describe(
                          'Period until first cycle starts. If None => no free trial'
                        )
                        .optional(),
                    })
                    .describe('Subscription detailed information.')
                    .optional(),
                  chargesDescription: z
                    .string()
                    .describe(
                      'Description of the charges that will be applied for subscription.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                })
                .describe('Subscription info.')
                .optional(),
              priceDescription: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                    )
                    .min(1)
                    .max(100)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
                )
                .optional(),
              depositAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
                )
                .optional(),
              locations: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Location id in the associated owner app.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    appId: z
                      .string()
                      .describe(
                        'Location owner app, if not provided then the site business info locations will be used.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe('Quantity for specific location.')
                      .min(1)
                      .max(100000)
                      .optional(),
                    name: z
                      .string()
                      .describe('Location name.')
                      .max(500)
                      .optional()
                      .nullable(),
                  })
                )
                .min(0)
                .max(5)
                .optional(),
              lineItemPrice: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Total price **after** catalog discounts and line item discounts.'
                )
                .optional(),
              taxableAddress: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({ addressType: z.never().optional() }),
                    z.object({
                      addressType: z
                        .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                        .describe(
                          'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                        ),
                    }),
                  ])
                )
                .describe('Address used for tax calculation.')
                .optional(),
              priceUndetermined: z
                .boolean()
                .describe(
                  'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
                )
                .optional(),
              fixedQuantity: z
                .boolean()
                .describe(
                  'Whether the line item quantity is fixed and cannot be changed.\n\nDefault: `false`'
                )
                .optional(),
              decimalQuantity: z
                .intersection(
                  z.object({
                    value: z
                      .string()
                      .describe(
                        'The decimal quantity the buyer / merchant entered.\n\nString-encoded decimal. The exact value is preserved — no\nrounding, no conversion to an integer.\n\nExamples: "2.75", "1.5", "0.75".'
                      )
                      .optional(),
                    description: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Human-readable, pre-formatted display string for the quantity. For example, "2.75 square meters of fake grass".\nAlways set alongside `value`; use this directly for display rather than composing your own string from `value` and `unit`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      measurementUnit: z.never().optional(),
                      otherUnit: z.never().optional(),
                    }),
                    z.object({
                      otherUnit: z.never().optional(),
                      measurementUnit: z
                        .enum([
                          'UNSPECIFIED',
                          'ML',
                          'CL',
                          'L',
                          'CBM',
                          'MG',
                          'G',
                          'KG',
                          'MM',
                          'CM',
                          'M',
                          'SQM',
                          'OZ',
                          'LB',
                          'FLOZ',
                          'PT',
                          'QT',
                          'GAL',
                          'IN',
                          'FT',
                          'YD',
                          'SQFT',
                        ])
                        .describe(
                          'Unit the value is expressed in, selected from the standard `measurementUnit` enum.'
                        ),
                    }),
                    z.object({
                      measurementUnit: z.never().optional(),
                      otherUnit: z
                        .string()
                        .describe(
                          'Free-form unit name for standard units not in the\n`measurementUnit` enum (e.g. "hour", "minute", "session").'
                        )
                        .min(1)
                        .max(50),
                    }),
                  ])
                )
                .describe(
                  'Decimal-quantity metadata. Set when the line represents a\nnon-integer purchase (e.g. 2.75 m² of fake grass).\n\nWhen set, the line item must have `fixedQuantity` = true and be set with `quantity` = 1.\nThe decimal value lives in `decimalQuantity.value`.'
                )
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              modifierGroups: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier group ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Modifier group name.')
                      .optional(),
                    modifiers: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Modifier ID.')
                            .min(1)
                            .max(36)
                            .optional(),
                          quantity: z
                            .number()
                            .int()
                            .describe('The quantity of this modifier.')
                            .min(1)
                            .max(100000)
                            .optional()
                            .nullable(),
                          label: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                )
                                .min(1)
                                .max(200)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                )
                                .min(1)
                                .max(400)
                                .optional()
                                .nullable(),
                            })
                            .describe('Primary display label for the modifier.')
                            .optional(),
                          details: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                )
                                .min(1)
                                .max(200)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                )
                                .min(1)
                                .max(400)
                                .optional()
                                .nullable(),
                            })
                            .describe('Additional details.')
                            .optional(),
                          price: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('The price of the modifier.')
                            .optional(),
                        })
                      )
                      .min(1)
                      .max(10)
                      .optional(),
                  })
                )
                .max(10)
                .optional(),
            })
          )
          .min(1)
          .max(100)
          .optional(),
        buyerInfo: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              email: z
                .string()
                .describe("Buyer's email address.")
                .email()
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                visitorId: z.never().optional(),
                memberId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                visitorId: z
                  .string()
                  .describe(
                    "Visitor ID. Returned when the buyer isn't a logged-in site member."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                visitorId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'Member ID. Returned when the buyer is a logged-in site member.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe('Buyer information.')
          .optional(),
        buyerLanguage: z
          .string()
          .describe(
            'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
          )
          .max(10)
          .optional()
          .nullable(),
        weightUnit: z
          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
          .describe("Weight measurement unit - defaults to site's weight unit.")
          .optional(),
        currency: z
          .string()
          .describe(
            'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
          )
          .optional()
          .nullable(),
        currencyConversionDetails: z
          .object({
            originalCurrency: z
              .string()
              .describe(
                "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
              )
              .optional()
              .nullable(),
            conversionRate: z
              .string()
              .describe(
                'The rate used for converting the original currency to the currency used for payment.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Multi-currency information of the subscription. conversionRate may reflect updated exchange rate on created order per recurring cycle.'
          )
          .optional(),
        taxIncludedInPrices: z
          .boolean()
          .describe(
            'Whether tax is included in line item prices.\n\nDefault: `false`'
          )
          .optional(),
        priceSummary: z
          .object({
            subtotal: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Subtotal of all line items, before discounts and before tax.'
              )
              .optional(),
            shipping: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total shipping price, before discounts and before tax.'
              )
              .optional(),
            tax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total tax applied to the order.')
              .optional(),
            discount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total discount amount applied to the order.')
              .optional(),
            total: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Order's total price after discounts and tax.")
              .optional(),
            totalAdditionalFees: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total additional fees before tax.')
              .optional(),
          })
          .describe('Order price summary.')
          .optional(),
        platformFeeSummary: z
          .object({
            total: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total sum of all platform fees.')
              .optional(),
            totalPassOn: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total amount of platform fees with `PASS_ON` charge type.'
              )
              .optional(),
            totalAbsorbed: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total amount of platform fees with `ABSORBED` charge type.'
              )
              .optional(),
            fees: z
              .array(
                z.object({
                  name: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe('Platform fee name.')
                    .optional(),
                  amount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Platform fee amount.')
                    .optional(),
                  lineItemId: z
                    .string()
                    .describe(
                      'ID of the line item the platform fee applies to.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  chargeType: z
                    .enum(['PASS_ON', 'ABSORBED'])
                    .describe('Platform fee charge type.')
                    .optional(),
                  percentageRate: z
                    .string()
                    .describe(
                      'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
                    )
                    .optional(),
                })
              )
              .max(300)
              .optional(),
          })
          .describe(
            'Summary of platform fees for this subscription contract, including totals by charge type and a breakdown of individual fees.'
          )
          .optional(),
        billingInfo: z
          .object({
            address: z
              .object({
                streetAddress: z
                  .object({
                    name: z.string().optional(),
                    number: z.string().optional(),
                  })
                  .optional(),
                city: z.string().optional().nullable(),
                subdivision: z.string().optional().nullable(),
                country: z.string().optional().nullable(),
                postalCode: z.string().optional().nullable(),
                addressLine2: z.string().optional().nullable(),
              })
              .describe('Address.')
              .optional(),
            contactDetails: z
              .object({
                firstName: z
                  .string()
                  .describe('First name.')
                  .max(100)
                  .optional()
                  .nullable(),
                lastName: z
                  .string()
                  .describe('Last name.')
                  .max(100)
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Phone number.')
                  .optional()
                  .nullable(),
                company: z
                  .string()
                  .describe('Company name.')
                  .max(1000)
                  .optional()
                  .nullable(),
                vatId: z
                  .object({
                    _id: z.string().describe("Customer's tax ID.").optional(),
                    type: z
                      .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                      .describe(
                        'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                      )
                      .optional(),
                  })
                  .describe(
                    'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                  )
                  .optional(),
              })
              .describe('Contact details.')
              .optional(),
          })
          .describe('Billing address and contact details.')
          .optional(),
        shippingInfo: z
          .object({
            carrierId: z
              .string()
              .describe(
                'App Def Id of external provider which was a source of shipping info'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            code: z
              .string()
              .describe(
                'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            title: z
              .string()
              .describe(
                'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
              )
              .max(250)
              .optional(),
            logistics: z
              .intersection(
                z.object({
                  deliveryTime: z
                    .string()
                    .describe(
                      'Expected delivery time in free text. For example, `"3-5 business days"`.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  instructions: z
                    .string()
                    .describe(
                      'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                  deliverByDate: z
                    .date()
                    .describe(
                      'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                    )
                    .optional()
                    .nullable(),
                  deliveryTimeSlot: z
                    .object({
                      from: z
                        .date()
                        .describe('Delivery slot starting time.')
                        .optional()
                        .nullable(),
                      to: z
                        .date()
                        .describe('Delivery slot ending time.')
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Expected delivery time slot with start and end times.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    shippingDestination: z.never().optional(),
                    pickupDetails: z.never().optional(),
                  }),
                  z.object({
                    pickupDetails: z.never().optional(),
                    shippingDestination: z
                      .object({
                        address: z
                          .object({
                            streetAddress: z
                              .object({
                                name: z.string().optional(),
                                number: z.string().optional(),
                              })
                              .optional(),
                            city: z.string().optional().nullable(),
                            subdivision: z.string().optional().nullable(),
                            country: z.string().optional().nullable(),
                            postalCode: z.string().optional().nullable(),
                            addressLine2: z.string().optional().nullable(),
                          })
                          .describe('Address.')
                          .optional(),
                        contactDetails: z
                          .object({
                            firstName: z
                              .string()
                              .describe('First name.')
                              .max(100)
                              .optional()
                              .nullable(),
                            lastName: z
                              .string()
                              .describe('Last name.')
                              .max(100)
                              .optional()
                              .nullable(),
                            phone: z
                              .string()
                              .describe('Phone number.')
                              .optional()
                              .nullable(),
                            company: z
                              .string()
                              .describe('Company name.')
                              .max(1000)
                              .optional()
                              .nullable(),
                            vatId: z
                              .object({
                                _id: z
                                  .string()
                                  .describe("Customer's tax ID.")
                                  .optional(),
                                type: z
                                  .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                                  .describe(
                                    'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                              )
                              .optional(),
                          })
                          .describe('Contact details.')
                          .optional(),
                      })
                      .describe(
                        "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                      ),
                  }),
                  z.object({
                    shippingDestination: z.never().optional(),
                    pickupDetails: z
                      .object({
                        address: z
                          .object({
                            streetAddress: z
                              .object({
                                name: z.string().optional(),
                                number: z.string().optional(),
                              })
                              .optional(),
                            city: z.string().optional().nullable(),
                            subdivision: z.string().optional().nullable(),
                            country: z.string().optional().nullable(),
                            postalCode: z.string().optional().nullable(),
                            addressLine2: z.string().optional().nullable(),
                          })
                          .describe('Pickup address.')
                          .optional(),
                        pickupMethod: z
                          .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                          .describe('Pickup method')
                          .optional(),
                      })
                      .describe(
                        'Pickup details for store pickup or pickup point orders.'
                      ),
                  }),
                ])
              )
              .describe('Shipping logistics.')
              .optional(),
            cost: z
              .object({
                price: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Shipping price for display purposes.')
                  .optional(),
                totalPriceBeforeTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total price of shipping after discounts (when relevant), and before tax.'
                  )
                  .optional(),
                totalPriceAfterTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Shipping price after all discounts (if any exist), and after tax.'
                  )
                  .optional(),
                taxDetails: z
                  .object({
                    taxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Taxable amount of this line item.')
                      .optional(),
                    taxRate: z
                      .string()
                      .describe(
                        'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                      )
                      .optional(),
                    totalTax: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe(
                        'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                      )
                      .optional(),
                  })
                  .describe('Tax details.')
                  .optional(),
                taxInfo: z
                  .object({
                    taxAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe(
                        'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                      )
                      .optional(),
                    taxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Amount for which tax is calculated.')
                      .optional(),
                    taxRate: z
                      .string()
                      .describe('Tax rate %, as a decimal point.')
                      .optional()
                      .nullable(),
                    taxGroupId: z
                      .string()
                      .describe('Tax group ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    taxIncludedInPrice: z
                      .boolean()
                      .describe(
                        'Indicates whether the price already includes tax.'
                      )
                      .optional(),
                    taxBreakdown: z
                      .array(
                        z.object({
                          jurisdiction: z
                            .string()
                            .describe(
                              'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                          rate: z
                            .string()
                            .describe(
                              'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                            )
                            .optional()
                            .nullable(),
                          taxAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Amount of tax calculated for this line item.'
                            )
                            .optional(),
                          taxType: z
                            .string()
                            .describe(
                              'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                          taxName: z
                            .string()
                            .describe(
                              'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                          jurisdictionType: z
                            .enum([
                              'UNDEFINED',
                              'COUNTRY',
                              'STATE',
                              'COUNTY',
                              'CITY',
                              'SPECIAL',
                            ])
                            .describe(
                              'Type of jurisdiction that taxes were calculated for.'
                            )
                            .optional(),
                          nonTaxableAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Non-taxable amount of the line item price.'
                            )
                            .optional(),
                          taxableAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Taxable amount of the line item price.')
                            .optional(),
                        })
                      )
                      .max(7)
                      .optional(),
                  })
                  .describe(
                    'Represents all the relevant tax details for a shipping.'
                  )
                  .optional(),
                discount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Shipping discount before tax.')
                  .optional(),
              })
              .describe('Shipping costs.')
              .optional(),
            region: z
              .object({
                name: z
                  .string()
                  .describe(
                    'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Shipping region.')
              .optional(),
          })
          .describe('Shipping info and selected shipping option details.')
          .optional(),
        appliedDiscounts: z
          .array(
            z.intersection(
              z.object({
                discountType: z
                  .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
                  .optional(),
                lineItemIds: z.array(z.string()).min(0).max(300).optional(),
                _id: z
                  .string()
                  .describe('Discount ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                lineItemDiscounts: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Line item ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      totalDiscount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Total discount amount for this line item.')
                        .optional(),
                    })
                  )
                  .max(300)
                  .optional(),
              }),
              z.xor([
                z.object({
                  coupon: z.never().optional(),
                  merchantDiscount: z.never().optional(),
                  discountRule: z.never().optional(),
                }),
                z.object({
                  merchantDiscount: z.never().optional(),
                  discountRule: z.never().optional(),
                  coupon: z
                    .object({
                      _id: z.string().describe('Coupon ID.').optional(),
                      code: z.string().describe('Coupon code.').optional(),
                      name: z.string().describe('Coupon name.').optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Coupon value.')
                        .optional(),
                    })
                    .describe('Coupon applied by the customer.'),
                }),
                z.object({
                  coupon: z.never().optional(),
                  discountRule: z.never().optional(),
                  merchantDiscount: z
                    .intersection(
                      z.object({
                        amount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Discount amount.')
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          discountReason: z.never().optional(),
                          description: z.never().optional(),
                        }),
                        z.object({
                          description: z.never().optional(),
                          discountReason: z
                            .enum([
                              'UNSPECIFIED',
                              'EXCHANGED_ITEMS',
                              'BILLING_ADJUSTMENT',
                            ])
                            .describe('Predefined discount reason.'),
                        }),
                        z.object({
                          discountReason: z.never().optional(),
                          description: z
                            .string()
                            .describe(
                              'Custom discount description as free text.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe('Discount applied manually by the merchant.'),
                }),
                z.object({
                  coupon: z.never().optional(),
                  merchantDiscount: z.never().optional(),
                  discountRule: z
                    .object({
                      _id: z
                        .string()
                        .describe('Discount rule ID')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      name: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Original discount rule name (in site's default language)."
                            )
                            .min(1)
                            .max(256)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                            )
                            .min(1)
                            .max(500)
                            .optional()
                            .nullable(),
                        })
                        .describe('Discount rule name')
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Discount value.')
                        .optional(),
                    })
                    .describe(
                      'Automatic discount applied by the system based on configured discount rules.'
                    ),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        channelInfo: z
          .object({
            type: z
              .enum([
                'UNSPECIFIED',
                'WEB',
                'POS',
                'EBAY',
                'AMAZON',
                'OTHER_PLATFORM',
                'WIX_APP_STORE',
                'WIX_INVOICES',
                'BACKOFFICE_MERCHANT',
                'WISH',
                'CLASS_PASS',
                'GLOBAL_E',
                'FACEBOOK',
                'ETSY',
                'TIKTOK',
                'FAIRE_COM',
                'PAYPAL_AGENTIC_CHECKOUT',
                'STRIPE_AGENTIC_CHECKOUT',
              ])
              .describe(
                'Sales channel that submitted the order.\n\nWhen creating an order via the API to record an external order, use the channel type that matches the original source. If no matching type exists, use `OTHER_PLATFORM`.'
              )
              .optional(),
            externalOrderId: z
              .string()
              .describe(
                'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
              )
              .max(100)
              .optional()
              .nullable(),
            externalOrderUrl: z
              .string()
              .describe(
                'URL to the order in the external system. Relevant when recording orders from external platforms.'
              )
              .max(300)
              .optional()
              .nullable(),
          })
          .describe(
            'Information about the sales channel that submitted this order.'
          )
          .optional(),
        customFields: z
          .array(
            z.object({
              value: z
                .any()
                .describe('Custom field value.')
                .optional()
                .nullable(),
              title: z
                .string()
                .describe('Custom field title.')
                .min(1)
                .max(500)
                .optional(),
              translatedTitle: z
                .string()
                .describe('Translated custom field title.')
                .min(1)
                .max(500)
                .optional()
                .nullable(),
            })
          )
          .max(100)
          .optional(),
        recipientInfo: z
          .object({
            address: z
              .object({
                streetAddress: z
                  .object({
                    name: z.string().optional(),
                    number: z.string().optional(),
                  })
                  .optional(),
                city: z.string().optional().nullable(),
                subdivision: z.string().optional().nullable(),
                country: z.string().optional().nullable(),
                postalCode: z.string().optional().nullable(),
                addressLine2: z.string().optional().nullable(),
              })
              .describe('Address.')
              .optional(),
            contactDetails: z
              .object({
                firstName: z
                  .string()
                  .describe('First name.')
                  .max(100)
                  .optional()
                  .nullable(),
                lastName: z
                  .string()
                  .describe('Last name.')
                  .max(100)
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Phone number.')
                  .optional()
                  .nullable(),
                company: z
                  .string()
                  .describe('Company name.')
                  .max(1000)
                  .optional()
                  .nullable(),
                vatId: z
                  .object({
                    _id: z.string().describe("Customer's tax ID.").optional(),
                    type: z
                      .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                      .describe(
                        'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                      )
                      .optional(),
                  })
                  .describe(
                    'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                  )
                  .optional(),
              })
              .describe('Contact details.')
              .optional(),
          })
          .describe(
            'Order recipient address and contact details.\n\nThis field may differ from the address in `shippingInfo.logistics` when:\n+ The chosen shipping option is pickup point or store pickup.\n+ No shipping option is selected.'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the subscription contract object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the [app dashboard](https://dev.wix.com/dc3/my-apps/) before they can be accessed with API calls.'
          )
          .optional(),
        title: z
          .string()
          .describe(
            'Subscription option title. For example, `"Monthly coffee Subscription"`.'
          )
          .min(0)
          .max(150)
          .optional()
          .nullable(),
        additionalFees: z
          .array(
            z.object({
              code: z
                .string()
                .describe("Additional fee's unique code for future processing.")
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Name of additional fee.')
                .min(1)
                .max(50)
                .optional(),
              price: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe("Additional fee's price.")
                .optional(),
              taxDetails: z
                .object({
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of this line item.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                    )
                    .optional(),
                  totalTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                    )
                    .optional(),
                })
                .describe('Tax details.')
                .optional(),
              taxInfo: z
                .object({
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                    )
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount for which tax is calculated.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax rate %, as a decimal point.')
                    .optional()
                    .nullable(),
                  taxGroupId: z
                    .string()
                    .describe('Tax group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  taxIncludedInPrice: z
                    .boolean()
                    .describe(
                      'Indicates whether the price already includes tax.'
                    )
                    .optional(),
                  taxBreakdown: z
                    .array(
                      z.object({
                        jurisdiction: z
                          .string()
                          .describe(
                            'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        rate: z
                          .string()
                          .describe(
                            'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                          )
                          .optional()
                          .nullable(),
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Amount of tax calculated for this line item.'
                          )
                          .optional(),
                        taxType: z
                          .string()
                          .describe(
                            'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        taxName: z
                          .string()
                          .describe(
                            'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        jurisdictionType: z
                          .enum([
                            'UNDEFINED',
                            'COUNTRY',
                            'STATE',
                            'COUNTY',
                            'CITY',
                            'SPECIAL',
                          ])
                          .describe(
                            'Type of jurisdiction that taxes were calculated for.'
                          )
                          .optional(),
                        nonTaxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Non-taxable amount of the line item price.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of the line item price.')
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe(
                  'Represents all the relevant tax details for additional fee.'
                )
                .optional(),
              providerAppId: z
                .string()
                .describe("SPI implementer's `appId`.")
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              priceBeforeTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe("Additional fee's price before tax.")
                .optional(),
              priceAfterTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe("Additional fee's price after tax.")
                .optional(),
              _id: z
                .string()
                .describe("Additional fee's id.")
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              lineItemIds: z.array(z.string()).optional(),
              source: z
                .enum([
                  'SERVICE_PLUGIN',
                  'ITEM',
                  'MANUAL',
                  'SHIPPING',
                  'PLATFORM',
                ])
                .describe('Specifies the entity that added the additional fee.')
                .optional(),
              taxableAddress: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({ addressType: z.never().optional() }),
                    z.object({
                      addressType: z
                        .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                        .describe(
                          'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                        ),
                    }),
                  ])
                )
                .describe(
                  'Address type tax was calculated against for this fee.'
                )
                .optional(),
            })
          )
          .max(100)
          .optional(),
        activities: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Activity ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                _createdDate: z
                  .date()
                  .describe('When the activity occurred.')
                  .optional()
                  .nullable(),
                author: z
                  .intersection(
                    z.object({}),
                    z.xor([
                      z.object({
                        userId: z.never().optional(),
                        appId: z.never().optional(),
                      }),
                      z.object({
                        appId: z.never().optional(),
                        userId: z
                          .string()
                          .describe(
                            'User ID. When the draft order was created by a Wix user.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          ),
                      }),
                      z.object({
                        userId: z.never().optional(),
                        appId: z
                          .string()
                          .describe(
                            'App ID. When the draft order was created by an app.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          ),
                      }),
                    ])
                  )
                  .describe("Identity of the draft order's initiator.")
                  .optional(),
                activityType: z
                  .enum(['UNKNOWN_ACTIVITY_TYPE', 'CHANGES_APPLIED'])
                  .describe('Activity type.')
                  .optional(),
              }),
              z.intersection(
                z.xor([
                  z.object({ contractTarget: z.never().optional() }),
                  z.object({
                    contractTarget: z
                      .object({
                        nextCycleNumber: z
                          .number()
                          .int()
                          .describe(
                            'Cycle number of the next billing cycle (1-based). Set by the caller; returned by the server on read.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Scopes the draft to the contract level, applying to all future cycles.'
                      ),
                  }),
                ]),
                z.xor([
                  z.object({ changesApplied: z.never().optional() }),
                  z.object({
                    changesApplied: z
                      .intersection(
                        z.object({
                          draftSubscriptionContractId: z
                            .string()
                            .describe(
                              'ID of the DraftSubscriptionContract that was committed.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          changes: z
                            .array(
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                  }),
                                  z.object({
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    lineItemChanged: z
                                      .object({
                                        _id: z
                                          .string()
                                          .describe('Line item ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                              )
                                              .min(1)
                                              .max(200)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe(
                                                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                              )
                                              .min(1)
                                              .max(400)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Item name after change.')
                                          .optional(),
                                        quantity: z
                                          .object({
                                            originalQuantity: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Original item quantity before the change.'
                                              )
                                              .min(0)
                                              .max(1000000)
                                              .optional(),
                                            newQuantity: z
                                              .number()
                                              .int()
                                              .describe(
                                                'New item quantity after the change.'
                                              )
                                              .min(0)
                                              .max(1000000)
                                              .optional(),
                                            diff: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Difference between new and original quantities.'
                                              )
                                              .min(1)
                                              .max(1000000)
                                              .optional(),
                                            deltaType: z
                                              .enum([
                                                'QUANTITY_INCREASED',
                                                'QUANTITY_DECREASED',
                                              ])
                                              .describe(
                                                'Direction of the quantity change.'
                                              )
                                              .optional(),
                                          })
                                          .describe('Item quantity change.')
                                          .optional(),
                                      })
                                      .describe('Line item was modified.'),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    lineItemAdded: z
                                      .object({
                                        _id: z
                                          .string()
                                          .describe('Line item ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                              )
                                              .min(1)
                                              .max(200)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe(
                                                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                              )
                                              .min(1)
                                              .max(400)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Item name.')
                                          .optional(),
                                        quantity: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Added or removed item quantity.'
                                          )
                                          .min(1)
                                          .max(1000000)
                                          .optional(),
                                      })
                                      .describe(
                                        'Line item was added to the contract.'
                                      ),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    lineItemRemoved: z
                                      .object({
                                        _id: z
                                          .string()
                                          .describe('Line item ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                              )
                                              .min(1)
                                              .max(200)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe(
                                                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                              )
                                              .min(1)
                                              .max(400)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Item name.')
                                          .optional(),
                                        quantity: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Added or removed item quantity.'
                                          )
                                          .min(1)
                                          .max(1000000)
                                          .optional(),
                                      })
                                      .describe(
                                        'Line item was removed from the contract.'
                                      ),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    discountAdded: z
                                      .object({
                                        _id: z
                                          .string()
                                          .describe('Discount ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                'Value in site default language.'
                                              )
                                              .min(1)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe('Translated value.')
                                              .min(1)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Discount name.')
                                          .optional(),
                                        totalAmount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe('Discount amount.')
                                          .optional(),
                                      })
                                      .describe(
                                        'Discount was added to the contract.'
                                      ),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    discountRemoved: z
                                      .object({
                                        _id: z
                                          .string()
                                          .describe('Discount ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                'Value in site default language.'
                                              )
                                              .min(1)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe('Translated value.')
                                              .min(1)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Discount name.')
                                          .optional(),
                                        totalAmount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe('Discount amount.')
                                          .optional(),
                                      })
                                      .describe(
                                        'Discount was removed from the contract.'
                                      ),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    additionalFeeAdded: z
                                      .object({
                                        _id: z
                                          .string()
                                          .describe('Additional fee ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                'Value in site default language.'
                                              )
                                              .min(1)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe('Translated value.')
                                              .min(1)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Additional fee name.')
                                          .optional(),
                                        totalAmount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe('Additional fee amount.')
                                          .optional(),
                                      })
                                      .describe(
                                        'Additional fee was added to the contract.'
                                      ),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    additionalFeeRemoved: z
                                      .object({
                                        _id: z
                                          .string()
                                          .describe('Additional fee ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                'Value in site default language.'
                                              )
                                              .min(1)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe('Translated value.')
                                              .min(1)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Additional fee name.')
                                          .optional(),
                                        totalAmount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe('Additional fee amount.')
                                          .optional(),
                                      })
                                      .describe(
                                        'Additional fee was removed from the contract.'
                                      ),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z
                                      .object({
                                        originalTotal: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            'Total price before the commit.'
                                          )
                                          .optional(),
                                        newTotal: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            'Total price after the commit.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Total price changed.'),
                                  }),
                                ])
                              )
                            )
                            .max(100)
                            .optional(),
                          reason: z
                            .string()
                            .describe(
                              'Reason for the edit, provided by the merchant (optional).'
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                        }),
                        z.xor([
                          z.object({ contractTarget: z.never().optional() }),
                          z.object({
                            contractTarget: z
                              .object({
                                nextCycleNumber: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Cycle number of the next billing cycle (1-based). Set by the caller; returned by the server on read.'
                                  )
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Scopes the draft to the contract level, applying to all future cycles.'
                              ),
                          }),
                        ])
                      )
                      .describe(
                        'Changes committed from a DraftSubscriptionContract.'
                      ),
                  }),
                ])
              )
            )
          )
          .max(500)
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
